import { Link } from 'react-router-dom';
import { useStore } from 'context/StoreContext';
import Button from 'components/Button';
import Container from 'components/Container';
import ResponsiveImage from 'components/ResponsiveImage';
import { ArrowRightIcon } from 'components/icons';

function Actions({ cta, link, inverse = false }) {
  return (
    <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-4">
      {cta && (
        <Button to={cta.href} size="lg" variant={inverse ? 'inverse' : 'primary'}>
          {cta.label}
        </Button>
      )}
      {link && (
        <Link
          to={link.href}
          className={`group inline-flex min-h-[44px] items-center gap-2 text-[15px] font-semibold underline-offset-4 hover:underline ${
            inverse ? 'text-white' : 'text-ink'
          }`}
        >
          {link.label}
          <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </Link>
      )}
    </div>
  );
}

function Split({ block }) {
  return (
    <section aria-labelledby="hero-title">
      <Container className="grid items-center gap-x-14 gap-y-8 pb-14 pt-0 sm:pt-6 lg:grid-cols-12 lg:pb-20 lg:pt-10">
        <div className="relative -mx-4 overflow-hidden bg-surface-alt sm:mx-0 sm:rounded-lg lg:order-2 lg:col-span-6 lg:col-start-7">
          <ResponsiveImage
            image={block.image}
            priority
            sizes="(min-width: 1024px) 50vw, 100vw"
            className="aspect-[4/5] h-auto w-full object-cover sm:aspect-[4/3] lg:aspect-[4/5] lg:max-h-[min(720px,calc(100vh-150px))]"
          />
        </div>
        <div className="lg:order-1 lg:col-span-6 lg:pr-6">
          {block.eyebrow && <p className="eyebrow mb-5 text-accent">{block.eyebrow}</p>}
          <h1
            id="hero-title"
            className="font-display text-[2.75rem] leading-[1.02] text-ink sm:text-6xl lg:text-[4.6rem]"
          >
            {block.title}
          </h1>
          {block.body && (
            <p className="mt-6 max-w-[34rem] text-pretty text-[17px] leading-relaxed text-muted lg:text-lg">
              {block.body}
            </p>
          )}
          <Actions cta={block.cta} link={block.link} />
        </div>
      </Container>
    </section>
  );
}

function FullBleed({ block }) {
  const image = block.imageWide || block.image;
  return (
    <section aria-labelledby="hero-title" className="on-dark relative isolate overflow-hidden bg-primary">
      <ResponsiveImage
        image={image}
        priority
        sizes="100vw"
        alt=""
        className="absolute inset-0 -z-10 h-full w-full object-cover"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-gradient-to-t from-black/75 via-black/30 to-black/5"
      />
      <Container className="flex min-h-[78vh] flex-col justify-end pb-12 pt-40 text-white sm:min-h-[640px] lg:min-h-[80vh] lg:pb-20">
        {block.eyebrow && <p className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-white/85">{block.eyebrow}</p>}
        <h1 id="hero-title" className="font-display max-w-4xl text-5xl leading-[0.98] sm:text-7xl lg:text-[6rem]">
          {block.title}
        </h1>
        {block.body && <p className="mt-6 max-w-xl text-pretty text-lg text-white/85">{block.body}</p>}
        <Actions cta={block.cta} link={block.link} inverse />
      </Container>
    </section>
  );
}

function Editorial({ block }) {
  const image = block.imageWide || block.image;
  return (
    <section aria-labelledby="hero-title" className="pb-6">
      <Container className="pt-10 lg:pt-16">
        {block.eyebrow && <p className="eyebrow mb-6">{block.eyebrow}</p>}
        <h1
          id="hero-title"
          className="font-display max-w-6xl text-[3.1rem] leading-[0.98] text-ink sm:text-7xl lg:text-[7rem]"
        >
          {block.title}
        </h1>
        <div className="mt-8 grid gap-6 lg:mt-12 lg:grid-cols-12">
          {block.body && (
            <p className="text-pretty text-lg text-muted lg:col-span-5 lg:col-start-8">{block.body}</p>
          )}
          <div className="lg:col-span-5 lg:col-start-8 lg:-mt-6">
            <Actions cta={block.cta} link={block.link} />
          </div>
        </div>
      </Container>
      <div className="mt-10 lg:mt-14">
        <ResponsiveImage
          image={image}
          priority
          sizes="100vw"
          className="aspect-[4/3] w-full object-cover sm:aspect-[16/9] lg:aspect-[21/9]"
        />
      </div>
    </section>
  );
}

/** Hero in the layout the theme asks for (theme.layout.hero). */
export default function HeroBlock({ block }) {
  const { theme } = useStore();
  if (theme.layout.hero === 'full-bleed') return <FullBleed block={block} />;
  if (theme.layout.hero === 'editorial') return <Editorial block={block} />;
  return <Split block={block} />;
}

export function HeroSkeleton() {
  return (
    <Container className="grid gap-8 pb-14 pt-6 lg:grid-cols-12 lg:pt-10" aria-hidden="true">
      <div className="-mx-4 aspect-[4/5] bg-surface-alt sm:mx-0 sm:aspect-[4/3] sm:rounded-lg lg:order-2 lg:col-span-6 lg:col-start-7 lg:aspect-[4/5] lg:max-h-[min(720px,calc(100vh-150px))]" />
      <div className="space-y-4 lg:order-1 lg:col-span-6 lg:self-center">
        <div className="h-3 w-40 rounded-sm bg-surface-alt" />
        <div className="h-14 w-full rounded-sm bg-surface-alt" />
        <div className="h-14 w-2/3 rounded-sm bg-surface-alt" />
      </div>
    </Container>
  );
}
